import { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { WizardFormData, initialWizardFormData, PropertyData } from "@/types/wizard";
import { useParcelLookup } from "@/hooks/useParcelLookup";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import WizardProgress from "./WizardProgress";
import Step1Address from "./steps/Step1Address";
import Step2HomeownerInfo from "./steps/Step2HomeownerInfo";
import Step3ADUType from "./steps/Step3ADUType";
import Step4Purpose from "./steps/Step4Purpose";
import Step5DesignStatus from "./steps/Step5DesignStatus";
import Step6SizeMaterials from "./steps/Step6SizeMaterials";
import Step7Verification from "./steps/Step7Verification";
import Step8Results from "./steps/Step8Results";

interface ADUWizardProps {
  isDark: boolean;
  compact?: boolean;
}

const ADUWizard = ({ isDark, compact = false }: ADUWizardProps) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<WizardFormData>(initialWizardFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { parcelData, lookupByAddress } = useParcelLookup();
  const { signUp } = useAuth();

  const updateFormData = (updates: Partial<WizardFormData>) => {
    setFormData(prev => ({ ...prev, ...updates }));
  };

  const handlePropertySelect = (data: PropertyData) => {
    updateFormData({ 
      propertyData: data, 
      projectAddress: data.address 
    });
    lookupByAddress(data.address);
  };

  const isStepValid = (): boolean => {
    switch (currentStep) {
      case 1: return !!formData.propertyData;
      case 2: return !!formData.firstName && !!formData.lastName && !!formData.phone;
      case 3: return !!formData.aduType;
      case 4: return formData.purposes.length > 0;
      case 5: return !!formData.designChoice;
      case 6: return !!formData.squareFootage || !!formData.finishLevel;
      case 7: return formData.isVerified && !!formData.email && !!formData.password && formData.password === formData.confirmPassword;
      default: return true;
    }
  };

  const handleNext = async () => {
    if (currentStep < 8 && isStepValid()) {
      // On Step 7, create the user account before proceeding
      if (currentStep === 7) {
        setIsSubmitting(true);
        try {
          const { error, data } = await signUp(formData.email, formData.password, {
            first_name: formData.firstName,
            last_name: formData.lastName,
            phone: formData.phone,
          });

          if (error) {
            toast.error(error.message || "Failed to create account");
            setIsSubmitting(false);
            return;
          }

          // Create lead in CRM
          const { error: leadError } = await supabase.from('crm_leads').insert({
            first_name: formData.firstName,
            last_name: formData.lastName,
            email: formData.email,
            phone: formData.phone,
            phone_verified: true,
            sms_consent: formData.smsConsent,
            property_address: formData.projectAddress,
            adu_type: formData.aduType,
            adu_purpose: formData.purposes,
            design_status: formData.designChoice,
            square_footage: parseInt(formData.squareFootage) || null,
            finish_level: formData.finishLevel,
            source: 'what-fits-my-lot-wizard',
            status: 'new',
          });

          if (leadError) {
            console.error('Error creating lead:', leadError);
          }

          toast.success("Account created successfully!");
          setCurrentStep(prev => prev + 1);
        } catch (err) {
          console.error('Error during signup:', err);
          toast.error("An unexpected error occurred");
        } finally {
          setIsSubmitting(false);
        }
      } else {
        setCurrentStep(prev => prev + 1);
      }
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(prev => prev - 1);
    }
  };

  // 3D Card styles with modern look
  const cardStyles: React.CSSProperties = {
    backgroundColor: isDark ? "#0f0f0f" : "#ffffff",
    boxShadow: isDark
      ? "12px 12px 24px #050505, -12px -12px 24px #151515, inset 0 1px 0 rgba(255,255,255,0.05)"
      : "12px 12px 24px #d0d0d0, -12px -12px 24px #ffffff, inset 0 1px 0 rgba(255,255,255,0.8)",
    border: isDark ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.03)",
  };

  const buttonBaseStyles = {
    backgroundColor: isDark ? "#151515" : "#f5f5f5",
    boxShadow: isDark
      ? "6px 6px 12px #0a0a0a, -6px -6px 12px #1a1a1a"
      : "6px 6px 12px #d8d8d8, -6px -6px 12px #ffffff",
    color: isDark ? "#ffffff" : "#1a1a1a",
  };

  const continueButtonStyles: React.CSSProperties = {
    background: "linear-gradient(135deg, #E4683B 0%, #f08a5d 100%)",
    boxShadow: "0 4px 15px rgba(228, 104, 59, 0.4), inset 0 1px 0 rgba(255,255,255,0.2)",
    color: "#ffffff",
  };

  const renderStep = () => {
    switch (currentStep) {
      case 1: return <Step1Address propertyData={formData.propertyData} onPropertySelect={handlePropertySelect} isDark={isDark} />;
      case 2: return <Step2HomeownerInfo formData={formData} updateFormData={updateFormData} isDark={isDark} />;
      case 3: return <Step3ADUType formData={formData} updateFormData={updateFormData} isDark={isDark} />;
      case 4: return <Step4Purpose formData={formData} updateFormData={updateFormData} isDark={isDark} />;
      case 5: return <Step5DesignStatus formData={formData} updateFormData={updateFormData} isDark={isDark} />;
      case 6: return <Step6SizeMaterials formData={formData} updateFormData={updateFormData} isDark={isDark} />;
      case 7: return <Step7Verification formData={formData} updateFormData={updateFormData} isDark={isDark} />;
      case 8: return <Step8Results formData={formData} parcelData={parcelData} isDark={isDark} />;
      default: return null;
    }
  };

  return (
    <motion.div 
      className={`${compact ? 'p-4 md:p-6' : 'p-6 md:p-10'} rounded-3xl`}
      style={cardStyles}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
    >
      <WizardProgress currentStep={currentStep} isDark={isDark} />
      
      <AnimatePresence mode="wait">
        <motion.div 
          key={currentStep}
          className={compact ? "min-h-[300px]" : "min-h-[400px]"}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {renderStep()}
        </motion.div>
      </AnimatePresence>

      {/* Navigation Buttons */}
      {currentStep < 8 && (
        <div 
          className="flex justify-between mt-8 pt-6"
          style={{ 
            borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
          }}
        >
          <motion.button
            onClick={handleBack}
            disabled={currentStep === 1}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-medium transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
            style={buttonBaseStyles}
            whileTap={{ scale: 0.95 }}
          >
            <ChevronLeft className="w-5 h-5 mr-2" />
            Back
          </motion.button>
          <motion.button
            onClick={handleNext}
            disabled={!isStepValid() || isSubmitting}
            className="flex items-center gap-2 px-8 py-3 rounded-xl font-semibold transition-all duration-200 hover:scale-105 disabled:opacity-40 disabled:hover:scale-100"
            style={{ 
              ...continueButtonStyles,
              opacity: (isStepValid() && !isSubmitting) ? 1 : 0.5,
            }}
            whileTap={{ scale: 0.95 }}
          >
            {isSubmitting ? (
              <span className="flex items-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Creating Account...
              </span>
            ) : (
              <>
                {currentStep === 7 ? "View Results" : "Continue"}
                <ChevronRight className="w-5 h-5 ml-2" />
              </>
            )}
          </motion.button>
        </div>
      )}
    </motion.div>
  );
};

export default ADUWizard;
